import { SelectOption } from '@stern-ui/ui';
import { add, format, getTime, isToday, isTomorrow, parse, startOfDay } from 'date-fns';
import { formatInTimeZone } from 'date-fns-tz';

// US can send from +10 minutes, UK can send from +2 hours
const usMinimumLeadTime = { minutes: 10 };
const ukMinimumLeadTime = { hours: 2 };
// US has available window of 17:00 - 21:00 UTC. No such window for UK.
const utcWindowStartHourMin = '17:00';
const utcWindowEndHourMin = '21:00';
const timeOptionIncrement = { minutes: 15 };
const lastTimeOptionValue = '23:45:00'; // the last 15 minute increment before midnight

// helper functions
const formatTimeValue = (dayTime: Date) => {
	// returns time in format HH:MM:SS
	return format(dayTime, 'HH:mm:ss');
};
const formatTimeLabel = (dayTime: Date) => {
	// returns time in format hh:MM AM/PM
	return format(dayTime, 'hh:mm aa');
};
const getUtcTimestamp = (dayTime: Date) => {
	// converts date to UTC timezone and returns a milliseconds timestamp
	const dateFormat = 'yyyy-MM-dd HH:mm:ss';
	const utcTimeDateStr = formatInTimeZone(dayTime, 'UTC', dateFormat);
	const utcTimeDate = parse(utcTimeDateStr, dateFormat, new Date());
	return getTime(utcTimeDate);
};
const getTimestampFromTime = (timeStr: string, baseDate: Date) => {
	// generates a date object merging a time string with a base date
	// returns a milliseconds timestamp to use for comparisons
	const dateFormat = 'HH:mm'; // HH:mm
	// parse uses the base date for any missing date time elements
	const dateTime = parse(timeStr, dateFormat, baseDate);
	return getTime(dateTime);
};
const isInRange = (
	dayTime: Date,
	startTimeUtc: number | undefined,
	endTimeUtc: number | undefined
) => {
	// checks if the given time is within the start / end times (if present)
	let isDisabled = false;
	const thisTimeHourMinUtc = getUtcTimestamp(dayTime);
	if (startTimeUtc) {
		isDisabled = thisTimeHourMinUtc < startTimeUtc;
	}
	if (endTimeUtc && !isDisabled) {
		isDisabled = thisTimeHourMinUtc > endTimeUtc;
	}
	return isDisabled;
};

export const getSurveySendStartTime = (isUs: boolean) => {
	// the earliest time a survey can be sent, returned as the user's local time
	return add(new Date(), isUs ? usMinimumLeadTime : ukMinimumLeadTime);
};

export const generateSurveyTimeOptions = (
	selectedDateValue: string | undefined,
	startTimeLocal: Date,
	isUs: boolean
) => {
	// returns a label/value array of all times in the day in 15 minute intervals
	const utcWindowStartDate = isUs
		? getTimestampFromTime(utcWindowStartHourMin, startTimeLocal)
		: undefined;
	const utcWindowEndDate = isUs
		? getTimestampFromTime(utcWindowEndHourMin, startTimeLocal)
		: undefined;
	// get a timestamp for the window start time
	let startTimeHourMin = utcWindowStartDate;
	if (selectedDateValue) {
		// date value has been selected
		const dateFormat = 'y-MM-dd'; // YYYY-MM-DD
		const selectedDate = parse(selectedDateValue, dateFormat, new Date());
		// if date selected is today or tomorrow we need to do aditional checks to ensure we only show available times
		if (isToday(selectedDate) || (isTomorrow(startTimeLocal) && isTomorrow(selectedDate))) {
			const localStartTimeUtcHourMinute = getUtcTimestamp(startTimeLocal);
			// if we have a UTC start time window then get the highest value from that or use local start time
			startTimeHourMin = startTimeHourMin
				? Math.max(localStartTimeUtcHourMinute, startTimeHourMin)
				: localStartTimeUtcHourMinute;
		}
	}
	const dayStart = startOfDay(startTimeLocal); // must use startTimeLocal so comparisons work
	let dateTimeValue = dayStart;
	let timeValue = formatTimeValue(dayStart);
	const timeOptions = [] as SelectOption[];
	while (timeValue !== lastTimeOptionValue) {
		// generate the option label, value and calculate if should be disabled
		timeValue = formatTimeValue(dateTimeValue);
		timeOptions.push({
			label: formatTimeLabel(dateTimeValue),
			value: timeValue,
			disabled: isInRange(dateTimeValue, startTimeHourMin, utcWindowEndDate),
		});
		// add 15 minutes until we reach the end time
		dateTimeValue = add(dateTimeValue, timeOptionIncrement);
	}
	return timeOptions;
};
